import {
  memo,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Loader2, Share } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  buildFullPageExportFilename,
  exportFullPage,
  FullPageExportShareAbortedError,
  FullPageExportStageError,
  FullPageExportSurfaceMissingError,
  resolveFullPageExportSurface,
  type FullPageExportDelivery,
  type FullPageExportFormat,
} from '../export/fullPage/index.js';
import { ADMIN_SHELL_ICON_BUTTON_CLASS } from './adminShellChrome.styles.js';
import { toastApi } from './Toast/toastApi.js';

export interface FullPageExportMenuProps {
  readonly tenantCode: string;
  readonly screenSlug: string;
}

/** Stable toast id — success/error for full-page export replace each other only. */
export const FULL_PAGE_EXPORT_TOAST_ID = 'full-page-export';

const TRIGGER_TEST_ID = 'enterprise-header-full-page-export';
const MENU_TEST_ID = 'enterprise-header-full-page-export-menu';
/** Download PDF — kept as legacy id for header/e2e contracts. */
const PDF_DOWNLOAD_TEST_ID = 'enterprise-header-full-page-export-pdf';
const PDF_SHARE_TEST_ID = 'enterprise-header-full-page-export-share-pdf';
/** Download PNG — kept as legacy id for header/e2e contracts. */
const PNG_DOWNLOAD_TEST_ID = 'enterprise-header-full-page-export-png';
const PNG_SHARE_TEST_ID = 'enterprise-header-full-page-export-share-png';

/** Map failures to pipeline stage for DEV diagnostics (UI stays Czech recovery only). */
function resolveExportFailureStage(err: unknown): string {
  if (err instanceof FullPageExportSurfaceMissingError) {
    return 'resolve-surface';
  }
  if (err instanceof FullPageExportStageError) {
    return err.stage;
  }
  return 'unknown';
}

/** Dev diagnostics only — never surface stacks in the user toast (AC-09). */
function logFullPageExportFailure(err: unknown): void {
  const stage = resolveExportFailureStage(err);
  if (process.env.NODE_ENV !== 'production') {
    console.error('[fullPageExport] failed', { stage, err });
  }
}

const TRIGGER_CLASS = [
  ADMIN_SHELL_ICON_BUTTON_CLASS,
  'disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:bg-transparent',
].join(' ');

const MENU_CLASS = [
  'absolute right-0 top-full z-[var(--z-popover)] mt-1 min-w-[13rem] overflow-hidden',
  'rounded-lg border border-[var(--color-an-border)] bg-[var(--color-an-surface)]',
  'py-1 shadow-[var(--elevation-2)]',
].join(' ');

const MENU_ITEM_CLASS = [
  'flex min-h-11 w-full items-center px-3 py-2.5 text-left text-sm',
  'text-[var(--color-an-text)]',
  'hover:bg-[var(--color-an-primary-soft)]',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px]',
  'focus-visible:outline-[var(--color-an-focus-ring)]',
  'disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:bg-transparent',
].join(' ');

function joinErrorRecovery(primary: string, recovery: string): string {
  return `${primary} ${recovery}`;
}

function successToastKey(
  format: FullPageExportFormat,
  delivery: FullPageExportDelivery,
): string {
  if (format === 'pdf') {
    return delivery === 'download'
      ? 'enterpriseHeader.fullPageExport.successDownloadPdf'
      : 'enterpriseHeader.fullPageExport.successSharePdf';
  }
  return delivery === 'download'
    ? 'enterpriseHeader.fullPageExport.successDownloadPng'
    : 'enterpriseHeader.fullPageExport.successSharePng';
}

function successToastDefault(
  format: FullPageExportFormat,
  delivery: FullPageExportDelivery,
): string {
  if (format === 'pdf') {
    return delivery === 'download'
      ? 'PDF bylo staženo do zařízení.'
      : 'PDF je připravené ke sdílení.';
  }
  return delivery === 'download'
    ? 'Obrázek byl stažen do zařízení.'
    : 'Obrázek je připravený ke sdílení.';
}

/**
 * Icon-only share/export for full-page PDF/PNG.
 * Menu offers Download and Share separately (never forces OS share for download).
 * Trigger + menu are chrome (`data-export-exclude`) so they are omitted from the capture.
 */
export const FullPageExportMenu = memo(
  ({ tenantCode, screenSlug }: FullPageExportMenuProps): JSX.Element => {
    const { t } = useTranslation('admin');
    const [open, setOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const exportingRef = useRef(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuId = useId();

    const ariaLabel = t('enterpriseHeader.fullPageExport.ariaLabel', {
      defaultValue: 'Sdílet nebo stáhnout stránku',
    });
    const exportingLabel = t('enterpriseHeader.fullPageExport.exporting', {
      defaultValue: 'Probíhá export stránky…',
    });
    const downloadPdfLabel = t('enterpriseHeader.fullPageExport.downloadPdf', {
      defaultValue: 'Stáhnout PDF',
    });
    const sharePdfLabel = t('enterpriseHeader.fullPageExport.sharePdf', {
      defaultValue: 'Sdílet PDF',
    });
    const downloadPngLabel = t('enterpriseHeader.fullPageExport.downloadPng', {
      defaultValue: 'Stáhnout PNG',
    });
    const sharePngLabel = t('enterpriseHeader.fullPageExport.sharePng', {
      defaultValue: 'Sdílet PNG',
    });
    const errorRecovery = t('enterpriseHeader.fullPageExport.errorRecovery', {
      defaultValue:
        'Zkuste to znovu. Pokud problém trvá, obnovte stránku a opakujte export.',
    });

    useEffect(() => {
      if (!open) {
        return undefined;
      }
      const firstItem = rootRef.current?.querySelector('[role="menuitem"]');
      if (firstItem instanceof HTMLElement) {
        firstItem.focus();
      }
      const onPointerDown = (event: MouseEvent): void => {
        if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
          setOpen(false);
        }
      };
      const onKeyDown = (event: globalThis.KeyboardEvent): void => {
        if (event.key === 'Escape') {
          event.preventDefault();
          setOpen(false);
          triggerRef.current?.focus();
        }
      };
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('keydown', onKeyDown);
      return (): void => {
        document.removeEventListener('mousedown', onPointerDown);
        document.removeEventListener('keydown', onKeyDown);
      };
    }, [open]);

    const toggle = useCallback((): void => {
      if (exportingRef.current) {
        return;
      }
      setOpen((prev) => !prev);
    }, []);

    const onMenuKeyDown = useCallback((event: KeyboardEvent<HTMLUListElement>): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    }, []);

    const runExport = useCallback(
      async (format: FullPageExportFormat, delivery: FullPageExportDelivery): Promise<void> => {
        if (exportingRef.current) {
          return;
        }
        exportingRef.current = true;
        setExporting(true);
        // Dismiss prior export outcome only — other system toasts stay (AC-09).
        toastApi.dismiss(FULL_PAGE_EXPORT_TOAST_ID);
        // Keep menu open while exporting so menuitems stay visible but disabled (G8).
        try {
          const element = resolveFullPageExportSurface();
          const filenameBase = buildFullPageExportFilename({ tenantCode, screenSlug });
          await exportFullPage({ element, filenameBase, format, delivery });
          toastApi({
            id: FULL_PAGE_EXPORT_TOAST_ID,
            message: t(successToastKey(format, delivery), {
              defaultValue: successToastDefault(format, delivery),
            }),
            variant: 'success',
          });
        } catch (err) {
          // User cancelled Web Share — neither success delivery nor controlled error (AC-07 / G13).
          if (
            err instanceof FullPageExportShareAbortedError ||
            (err instanceof Error &&
              err.name === 'FullPageExportShareAbortedError' &&
              'aborted' in err &&
              (err as { readonly aborted?: unknown }).aborted === true)
          ) {
            return;
          }
          logFullPageExportFailure(err);
          const missing = err instanceof FullPageExportSurfaceMissingError;
          toastApi({
            id: FULL_PAGE_EXPORT_TOAST_ID,
            message: joinErrorRecovery(
              t(
                missing
                  ? 'enterpriseHeader.fullPageExport.errorMissingSurface'
                  : 'enterpriseHeader.fullPageExport.errorFailed',
                {
                  defaultValue: missing
                    ? 'Export se nepodařil, protože se nenašel obsah stránky.'
                    : 'Export stránky se nepodařil.',
                },
              ),
              errorRecovery,
            ),
            variant: 'error',
          });
        } finally {
          exportingRef.current = false;
          setExporting(false);
          setOpen(false);
        }
      },
      [errorRecovery, screenSlug, t, tenantCode],
    );

    const onSelect = useCallback(
      (format: FullPageExportFormat, delivery: FullPageExportDelivery): void => {
        if (exportingRef.current) {
          return;
        }
        void runExport(format, delivery);
      },
      [runExport],
    );

    const triggerName = exporting ? exportingLabel : ariaLabel;

    return (
      <div
        ref={rootRef}
        className="relative inline-flex shrink-0"
        data-export-exclude="chrome"
        role="presentation"
      >
        <button
          ref={triggerRef}
          type="button"
          className={TRIGGER_CLASS}
          aria-label={triggerName}
          title={triggerName}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={menuId}
          aria-busy={exporting || undefined}
          disabled={exporting}
          data-testid={TRIGGER_TEST_ID}
          data-export-exclude="chrome"
          onClick={toggle}
        >
          {exporting ? (
            <Loader2 className="h-5 w-5 animate-spin stroke-[1.75]" aria-hidden />
          ) : (
            <Share className="h-5 w-5 stroke-[1.75]" aria-hidden />
          )}
        </button>
        {open ? (
          <ul
            id={menuId}
            role="menu"
            aria-label={ariaLabel}
            data-testid={MENU_TEST_ID}
            data-export-exclude="chrome"
            className={MENU_CLASS}
            onKeyDown={onMenuKeyDown}
          >
            <li role="none">
              <button
                type="button"
                role="menuitem"
                data-testid={PDF_DOWNLOAD_TEST_ID}
                className={MENU_ITEM_CLASS}
                disabled={exporting}
                onClick={() => {
                  onSelect('pdf', 'download');
                }}
              >
                {downloadPdfLabel}
              </button>
            </li>
            <li role="none">
              <button
                type="button"
                role="menuitem"
                data-testid={PDF_SHARE_TEST_ID}
                className={MENU_ITEM_CLASS}
                disabled={exporting}
                onClick={() => {
                  onSelect('pdf', 'share');
                }}
              >
                {sharePdfLabel}
              </button>
            </li>
            <li role="none">
              <button
                type="button"
                role="menuitem"
                data-testid={PNG_DOWNLOAD_TEST_ID}
                className={MENU_ITEM_CLASS}
                disabled={exporting}
                onClick={() => {
                  onSelect('png', 'download');
                }}
              >
                {downloadPngLabel}
              </button>
            </li>
            <li role="none">
              <button
                type="button"
                role="menuitem"
                data-testid={PNG_SHARE_TEST_ID}
                className={MENU_ITEM_CLASS}
                disabled={exporting}
                onClick={() => {
                  onSelect('png', 'share');
                }}
              >
                {sharePngLabel}
              </button>
            </li>
          </ul>
        ) : null}
      </div>
    );
  },
);

FullPageExportMenu.displayName = 'FullPageExportMenu';
